import { EmailEditor } from '@nile/banyan/src/Components/Editors';
import {
  getHeaderValue,
  withErrorBoundary,
  getTranslatedText,
  EventEmitter,
  fetchApi,
  Editor
} from '@nile/components';
import envConfig from '@nile/home/src/Config/env';
import {
  Button,
  ButtonGroup,
  Checkbox,
  Card
} from '@superops/ui-design-system';
import React, { useRef, useState, useEffect } from 'react';
import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css';
import { FormattedMessage, injectIntl, useIntl } from 'react-intl';
import { pick } from 'lodash';
import styled from 'styled-components';
import {
  dompurifier,
  formatContentWithMentionsAndPlaceholders
} from '@nile/components/src/Utils/helpers';
import SuperEditor from '@nile/components/src/Components/RichTextEditor/SuperEditor';

const SuperEditorComponent = (props) => {
  const { editorConfig, details, uploadParams } = props;

  const intl = useIntl();

  return (
    <SuperEditor
      focusEditor
      details={
        details || {
          operation: 'UPDATE'
        }
      }
      title="Add Attachments"
      description="Upload/drop files here"
      allowMultiple
      toolbarSticky={false}
      config={{
        htmlUntouched: true
      }}
      allowMentions
      storeConfig={editorConfig}
      placeholder={getTranslatedText(
        intl,
        'ticket.details.editNote.placeholder'
      )}
      module={props?.module || 'TICKET_NOTIFICATION_TEMPLATE'}
      attachmentUploadUrl={
        props?.attachmentUploadUrl || `${envConfig.fileRoot}/upload`
      }
      uploadParams={uploadParams}
    />
  );
};

const EditorWrapper = (props) => {
  const {
    template,
    updateTemplate,
    handleFileAttachments,
    handleAttachmentsDeletion,
    editorRef,
    isUploading,
    isSuperEditor,
    editorConfig
  } = props;
  const intl = useIntl();
  if (isSuperEditor) {
    return <SuperEditorComponent {...props} />;
  }

  return (
    <Editor
      focusEditor
      template={template}
      updateTemplate={updateTemplate}
      handleFileAttachments={handleFileAttachments}
      handleAttachmentsDeletion={handleAttachmentsDeletion}
      placeholder={getTranslatedText(
        intl,
        'ticket.details.editNote.placeholder'
      )}
      module={props?.module || 'TICKET_NOTIFICATION_TEMPLATE'}
      details={
        props?.details || {
          operation: 'UPDATE'
        }
      }
      editorRef={editorRef}
      title="Add Attachments"
      description="Upload/drop files here"
      allowMultiple
      toolbarSticky={false}
      config={{
        htmlUntouched: true
      }}
      allowMentions
      isUploading={isUploading}
    />
  );
};

const EditorHolder = styled.div`
  #quill-rt-editor {
    height: 100%;
    .quill {
      min-height: 200px;
      .ql-container {
        border: none;
        min-height: inherit;
        .ql-editor {
          min-height: inherit;
        }
      }
    }
    .ql-toolbar {
      border: none;
    }
  }
  .ql-editor.ql-blank::before {
    left: 10px;
  }
`;

const EditNoteContainer = (props) => {
  const { intl, message } = props;
  const [isDisabled, setIsDisabled] = useState(false);
  const PrivacyType =
    props?.privacyType || message?.privacyType || message?.data?.privacyType;
  const DEFAULT_IS_PUBLIC = PrivacyType === 'PUBLIC';
  const [isPublic, setIsPublic] = useState(DEFAULT_IS_PUBLIC);
  const [isInlineImageUploading, setIsInlineImageUploading] = useState(false);

  const [template, setTemplate] = useState({
    attachments: props.message?.attachments || [],
    content: props.message?.message,
    inlineImages: props.message?.inlineImages || [],
    mentions: props.message?.mentions || []
  });

  const templateByRef = useRef(null);
  templateByRef.current = template;

  const editorRef = useRef(null);

  const [isUploading, setIsUploading] = useState(false);

  useEffect(() => {
    EventEmitter.subscribe('imageUploading', setIsInlineImageUploading);
    return function cleanup() {
      EventEmitter.unsubscribe('imageUploading');
    };
  }, []);

  const updateTemplate = (obj) => {
    const updatedTemplate = {
      ...templateByRef.current,
      content: obj?.content,
      inlineImages: obj?.inlineImages,
      mentions: obj?.mentions
    };
    setTemplate(updatedTemplate);
    if (props?.updateNotesContent) {
      props.updateNotesContent(updatedTemplate);
    }
  };

  const handleFileAttachments = (files, callback) => {
    setIsUploading(true);
    const attachmentUrl = `${envConfig.fileRoot}/upload`;
    const formData = new FormData();
    formData.append(
      'module',
      props?.uploadParams?.module || 'TICKET_NOTE_ATTACHMENT'
    );
    formData.append(
      'details',
      props?.uploadParams?.details ||
        JSON.stringify({
          ticketId: props?.message?.ticketId
        })
    );

    files.forEach((file, idx) => {
      formData.append(`files`, file);
    });

    const requestOptions = {
      method: 'POST',
      credentials: 'include',
      body: formData,
      headers: getHeaderValue()
    };
    fetchApi(attachmentUrl, requestOptions)
      .then((response) => {
        return response.json();
      })
      .then((response) => {
        setIsUploading(false);

        const addedAttachments = templateByRef.current?.addAttachments || [];
        const addAttachments = response.data.map((att) => ({
          ...pick(att, ['fileName', 'fileSize', 'originalFileName'])
        }));

        const updatedTemplate = {
          ...templateByRef.current,
          attachments: [
            ...templateByRef.current?.attachments,
            ...addAttachments
          ],
          addAttachments: [...addedAttachments, ...addAttachments]
        };
        setTemplate(updatedTemplate);
        if (props?.updateNotesContent) {
          props.updateNotesContent(updatedTemplate);
        }
        if (callback) {
          callback();
        }
      });
  };

  const handleAttachmentsDeletion = (obj) => {
    const updatedAttachment = templateByRef.current.attachments.filter(
      (el) => el.fileName !== obj
    );

    const deletedFile = templateByRef.current?.attachments.filter(
      (el) => el.fileName === obj
    );

    let addAttachments = templateByRef.current?.addAttachments || [];

    if (addAttachments.length) {
      addAttachments = templateByRef.current?.addAttachments.filter(
        (el) => el.fileName !== obj
      );
    }

    const updatedTemplate = {
      ...templateByRef.current,
      attachments: updatedAttachment,
      addAttachments: addAttachments,
      deleteAttachments: templateByRef.current?.deleteAttachments
        ? templateByRef.current.deleteAttachments.concat(deletedFile)
        : deletedFile
    };
    setTemplate(updatedTemplate);
    if (props?.updateNotesContent) {
      props.updateNotesContent(updatedTemplate);
    }
  };

  const onMessageSubmit = () => {
    setIsDisabled(true);
    const {
      content,
      mentions,
      inlineImages,
      addAttachments,
      deleteAttachments
    } = templateByRef.current;
    const onSave = props.saveNote || props?.editNote;

    const formattedContentWithPlaceholders = formatContentWithMentionsAndPlaceholders(
      content
    );

    if (onSave) {
      onSave({
        variables: {
          input: {
            noteId: props.message?.data?.noteId,
            content: dompurifier(formattedContentWithPlaceholders),
            mentions: mentions,
            inlineImages: inlineImages,
            addAttachments: addAttachments,
            deleteAttachments: deleteAttachments,
            privacyType: !isPublic ? 'PRIVATE' : 'PUBLIC'
          }
        }
      }).then(() => {
        setIsDisabled(false);
        if (props.setEditState) {
          props.setEditState(false);
        }
      });
    }
  };

  const updateNoteAsPublic = (val) => {
    setIsPublic(val);
    if (props?.updatePrivacyType) {
      props.updatePrivacyType(val);
    }
  };

  return (
    <div className="edit-note-wrapper">
      <Card variant="attention" noShadow>
        <div className="fx fw">
          <div className="mlauto">
            <Checkbox
              checked={isPublic}
              onChange={updateNoteAsPublic}
              label={getTranslatedText(
                intl,
                'workItem.details.note.mark.as.public'
              )}
            />
          </div>
        </div>
        <EditorHolder>
          <EditorWrapper
            focusEditor
            template={templateByRef.current}
            updateTemplate={updateTemplate}
            handleFileAttachments={handleFileAttachments}
            handleAttachmentsDeletion={handleAttachmentsDeletion}
            placeholder={getTranslatedText(
              intl,
              'ticket.details.editNote.placeholder'
            )}
            module={props?.module || 'TICKET_NOTIFICATION_TEMPLATE'}
            details={
              props?.details || {
                operation: 'UPDATE'
              }
            }
            editorRef={editorRef}
            title="Add Attachments"
            description="Upload/drop files here"
            allowMultiple
            toolbarSticky={false}
            config={{
              htmlUntouched: true
            }}
            allowMentions
            isUploading={isUploading}
            isSuperEditor={props?.isSuperEditor}
            editorConfig={props?.editorConfig}
            attachmentUrl={`${envConfig.fileRoot}/upload`}
            uploadParams={props?.uploadParams}
          />
        </EditorHolder>
        <div
          className="fx end jend save-card"
          id="actionsContinerActionsHolder"
        >
          <div className="fx">
            <ButtonGroup>
              <div className="cancel-edit-btn">
                <Button
                  onClick={props?.onCancel}
                  plain
                  disabled={
                    props.editNoteLoading ||
                    isUploading ||
                    isInlineImageUploading
                  }
                >
                  <FormattedMessage id="common.cancel" />
                </Button>
              </div>
              <div className="save-btn">
                <Button
                  loading={props.editNoteLoading}
                  plain
                  disabled={isDisabled || isUploading || isInlineImageUploading}
                  onClick={onMessageSubmit}
                >
                  <FormattedMessage id="common.save" />
                </Button>
              </div>
            </ButtonGroup>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default withErrorBoundary(injectIntl(EditNoteContainer));
